/**
 * Add/Edit Member-profile-field dialog (DNKADM-0058; reworked for operators by
 * DNKADM-0270) — a MUI `Dialog` that creates/updates a `v2_member_profile_field`
 * via `createMemberProfileField` / `updateMemberProfileField` (handoff §5.13).
 *
 * **Outcome framing (DNKADM-0270).** The dialog is organized around the thing an
 * operator actually wants — "members can edit this on their profile" — not the
 * registry internals:
 *  - **Label first**; the stable `fieldKey` is AUTO-DERIVED from it (slugified)
 *    and only editable under the collapsed "Advanced" section, so an operator
 *    never has to invent a key. Server uniqueness (`409`) stays the real gate.
 *  - **"Members can edit this field"** is prominent, with plain-language ON/OFF
 *    consequences (ON → the field appears in the member's Preferences card and
 *    they can change it; OFF → hidden from members). Same term as the list
 *    column — no "Member-writable" jargon.
 *  - **Ownership defaults to "Managed here" (`DINK`) for a NEW field** — the
 *    right choice for a member preference (the pre-0270 External default steered
 *    operators to a config that never worked). Ownership + the contact-system
 *    key live under Advanced; the key is shown ONLY for External ownership.
 *    (The DATA-level safe default in `normalizeFieldOwnership` — malformed rows
 *    degrade to External — is unchanged; this flips only the new-field FORM
 *    default. The wire always carries an explicit ownership.)
 *  - **Option types are `single_options` ("Single choice") / `multi_options`
 *    ("Multiple choice")** (DNKADM-0275; the backend's wire vocabulary per
 *    DNKAPI-0439 — the old `select` value normalized to TEXT server-side and is
 *    gone). The options editor (add/remove, ≥1 required, trim + dedupe) shows
 *    ONLY for an option type with **DINK ("Managed here") ownership**: a
 *    GHL-owned option field's choices come from the contact system's picklist,
 *    and the backend 400s on ANY `options` key sent for it — even `[]` — so
 *    this dialog never emits one there. On edit, `options` is sent only when
 *    the choices actually changed (an omitted key leaves stored options
 *    untouched); leaving DINK-option state (type or ownership change mid-edit)
 *    drops `options` from the payload entirely.
 *
 * White-label (§4.0) unchanged: ownership renders via `fieldOwnershipLabel`, the
 * contact key via `CONTACT_SYSTEM_FIELD_KEY_LABEL` — the raw brand NEVER renders.
 * `isActive` is NOT set here — activation is the list's affordance (§4.3).
 *
 * Client validation blocks Save while the label (or an Advanced-edited key) is
 * empty and WARNS (does not block) on a duplicate key; a validation `400` / a
 * unique `409` renders INLINE, anything else raises an error toast
 * (`classifyFormError`).
 */
import { useId, useState } from 'react';
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { IconAction } from '@/components/IconAction';
import { Icon } from '@/theme';
import {
  createMemberProfileField,
  updateMemberProfileField,
  FIELD_TYPES,
  FIELD_OWNERSHIPS,
  fieldTypeLabel,
  isOptionFieldType,
  MEMBER_PROFILE_FIELD_OPTIONS_SUPPORTED,
  type FieldOwnership,
  type FieldType,
  type MemberProfileField,
  type MemberProfileFieldInput,
} from '@/api/memberProfileFields';
import {
  fieldOwnershipLabel,
  CONTACT_SYSTEM_FIELD_KEY_LABEL,
} from '@/utils/whitelabel';
import type { ShowToast } from '@/components/toastContext';
import { classifyFormError } from '@/api/screenError';
import { deriveFieldKey } from './fieldKey';

export interface MemberProfileFieldDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** The field being edited, or `null` to add a new one. */
  field: MemberProfileField | null;
  /** The field keys already in use (for the client-side uniqueness WARNING). */
  existingKeys: string[];
  /**
   * Seed "Members can edit this field" ON for a NEW field (the guided
   * empty-state path, DNKADM-0270). Ignored on edit.
   */
  initialMemberEditable?: boolean;
  /** Close the dialog without saving. */
  onClose: () => void;
  /** Called after a successful create/update so the caller can refetch the list. */
  onSaved: () => void;
  /** Raise a non-blocking toast (upstream-error). */
  showToast: ShowToast;
}

export function MemberProfileFieldDialog(props: MemberProfileFieldDialogProps) {
  // Remount the inner form per open / per edited field so its inputs reseed.
  if (!props.open) return null;
  return <MemberProfileFieldDialogForm key={props.field?.id ?? 'new'} {...props} />;
}

/** Trim a string and return `undefined` when empty (omit the optional key). */
function optional(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/**
 * Normalize an option list the way the backend validates it (DNKAPI-0439):
 * trim each entry, drop blanks, drop duplicates (post-trim exact match,
 * first occurrence wins, order preserved) — so the FE never submits a list
 * the server would 400 on for blanks/dups.
 */
function normalizeOptions(options: readonly string[]): string[] {
  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const entry of options) {
    const value = entry.trim();
    if (value === '' || seen.has(value)) continue;
    seen.add(value);
    normalized.push(value);
  }
  return normalized;
}

/** Order-sensitive list equality (option order is part of the stored data). */
function sameOptions(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value, i) => value === b[i]);
}

interface FieldErrors {
  fieldKey?: string;
  fieldLabel?: string;
  options?: string;
}

/** The plain-language ownership explanations (brand-neutral, §4.0). */
function ownershipHelp(ownership: FieldOwnership): string {
  return ownership === 'DINK'
    ? 'Stored by this platform — the usual choice for a member preference.'
    : 'Read from and written to your contact system.';
}

function MemberProfileFieldDialogForm({
  field,
  existingKeys,
  initialMemberEditable,
  onClose,
  onSaved,
  showToast,
}: Omit<MemberProfileFieldDialogProps, 'open'>) {
  const ids = useId();
  const isEdit = field !== null;

  const [fieldLabel, setFieldLabel] = useState(field?.fieldLabel ?? '');
  const [fieldKey, setFieldKey] = useState(field?.fieldKey ?? '');
  // Once the operator edits the key by hand (or we're editing an existing row),
  // the label stops driving it — a stored key must never drift under a rename.
  const [keyTouched, setKeyTouched] = useState(isEdit);
  const [fieldType, setFieldType] = useState<FieldType>(field?.fieldType ?? 'text');
  // DNKADM-0270: a NEW field defaults to "Managed here" — the member-preference
  // case this dialog exists for. (Pre-0270 the External default steered
  // operators into configs that never worked.)
  const [fieldOwnership, setFieldOwnership] = useState<FieldOwnership>(
    field?.fieldOwnership ?? 'DINK',
  );
  const [isMemberWritable, setIsMemberWritable] = useState(
    field?.isMemberWritable ?? initialMemberEditable ?? false,
  );
  const [contactKey, setContactKey] = useState(field?.ghlCustomFieldKey ?? '');
  // Choice-field options (behind MEMBER_PROFILE_FIELD_OPTIONS_SUPPORTED).
  const [options, setOptions] = useState<string[]>(field?.options ?? []);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const effectiveKey = (keyTouched ? fieldKey : deriveFieldKey(fieldLabel)).trim();
  // The own key (on edit) is excluded so editing a row doesn't warn against itself.
  const duplicateKey =
    effectiveKey !== '' &&
    existingKeys.some(
      (k) => k.toLowerCase() === effectiveKey.toLowerCase() && k !== field?.fieldKey,
    );

  const optionsAvailable = MEMBER_PROFILE_FIELD_OPTIONS_SUPPORTED;
  // The options editor is for a DINK-owned ("Managed here") option field ONLY
  // (DNKADM-0275): a GHL-owned option field's choices are the contact system's
  // picklist, and the backend 400s on ANY `options` key for it — even `[]`.
  const showOptionsEditor =
    optionsAvailable && isOptionFieldType(fieldType) && fieldOwnership === 'DINK';

  function validate(): MemberProfileFieldInput | null {
    const errors: FieldErrors = {};
    const tLabel = fieldLabel.trim();
    if (tLabel === '') errors.fieldLabel = 'Enter a label — this is what members see.';
    if (effectiveKey === '') {
      // Only reachable when the label is blank too, or an Advanced edit blanked
      // the key by hand.
      errors.fieldKey = 'Enter a field key (or a label to derive one from).';
    }
    const normalizedOptions = normalizeOptions(options);
    if (showOptionsEditor && normalizedOptions.length === 0) {
      errors.options = 'Add at least one choice members can pick.';
    }
    if (errors.fieldLabel || errors.fieldKey || errors.options) {
      setFieldErrors(errors);
      return null;
    }
    setFieldErrors({});

    const input: MemberProfileFieldInput = {
      fieldKey: effectiveKey,
      fieldLabel: tLabel,
      fieldType,
      fieldOwnership,
      isMemberWritable,
    };
    // The contact-system key is meaningful ONLY for an External field — a
    // Managed-here field never sends one (DNKADM-0270).
    if (fieldOwnership !== 'DINK') {
      const key = optional(contactKey);
      if (key !== undefined) input.ghlCustomFieldKey = key;
    }
    // `options` enters the payload ONLY from the visible editor state (a stale
    // list from a mid-edit type/ownership change can never leak — the backend
    // 400s on options outside DINK-option state, DNKAPI-0439), and on edit only
    // when the choices actually changed: an omitted key leaves stored options
    // untouched, which is the correct no-op for a label/writable-only edit.
    if (
      showOptionsEditor &&
      (!isEdit || !sameOptions(normalizedOptions, field?.options ?? []))
    ) {
      input.options = normalizedOptions;
    }
    return input;
  }

  async function handleSave() {
    setServerError(null);
    const input = validate();
    if (!input) return;
    setSaving(true);
    try {
      if (isEdit && field) await updateMemberProfileField(field.id, input);
      else await createMemberProfileField(input);
      onSaved();
    } catch (err) {
      const view = classifyFormError(
        err,
        'The field couldn’t be saved. Please try again.',
      );
      if (view.kind === 'inline') setServerError(view.message);
      else showToast(view.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  const titleId = `${ids}-title`;
  const typeLabelId = `${ids}-type-label`;
  const ownershipLabelId = `${ids}-ownership-label`;

  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth>
      <DialogTitle id={titleId}>
        {isEdit ? 'Edit profile field' : 'Add profile field'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <TextField
            label="Label"
            value={fieldLabel}
            onChange={(e) => setFieldLabel(e.target.value)}
            disabled={saving}
            size="small"
            required
            fullWidth
            placeholder="Preferred court, Handedness, …"
            error={Boolean(fieldErrors.fieldLabel)}
            helperText={
              fieldErrors.fieldLabel ?? 'What members (and staff) see this field called.'
            }
            slotProps={{ htmlInput: { 'aria-label': 'Label' } }}
          />

          <FormControl size="small" fullWidth disabled={saving}>
            <InputLabel id={typeLabelId}>Type</InputLabel>
            <Select
              labelId={typeLabelId}
              label="Type"
              value={fieldType}
              onChange={(e) => setFieldType(e.target.value as FieldType)}
              slotProps={{ input: { 'aria-label': 'Type' } }}
            >
              {FIELD_TYPES.map((type) =>
                isOptionFieldType(type) && !optionsAvailable ? (
                  // Disabled-with-explanation, never hidden (DNKADM-0270
                  // §Backend Dependency; dead while the support flag is true —
                  // kept as the flip-back state).
                  <MenuItem key={type} value={type} disabled>
                    {fieldTypeLabel(type)} — needs a platform update before
                    choices can be defined
                  </MenuItem>
                ) : (
                  <MenuItem key={type} value={type}>
                    {fieldTypeLabel(type)}
                  </MenuItem>
                ),
              )}
            </Select>
            {!optionsAvailable && (
              <FormHelperText>
                Need a set of choices? Use Text for now — you can change the type later.
              </FormHelperText>
            )}
            {optionsAvailable && isOptionFieldType(fieldType) && fieldOwnership !== 'DINK' && (
              // A GHL-owned option field's choices come from the contact
              // system — nothing to manage here (and nothing is ever sent).
              <FormHelperText>
                This field’s choices come from your contact system and are managed there.
              </FormHelperText>
            )}
          </FormControl>

          {showOptionsEditor && (
            <Stack spacing={1}>
              <Typography sx={{ fontWeight: 500, fontSize: 13.5 }}>Choices</Typography>
              {options.map((option, index) => (
                <Stack key={index} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <TextField
                    value={option}
                    onChange={(e) =>
                      setOptions((prev) =>
                        prev.map((o, i) => (i === index ? e.target.value : o)),
                      )
                    }
                    disabled={saving}
                    size="small"
                    fullWidth
                    slotProps={{ htmlInput: { 'aria-label': `Choice ${index + 1}` } }}
                  />
                  <IconAction
                    size="small"
                    label={`Remove choice ${index + 1}`}
                    disabled={saving}
                    onClick={() =>
                      setOptions((prev) => prev.filter((_, i) => i !== index))
                    }
                  >
                    <Icon name="close" />
                  </IconAction>
                </Stack>
              ))}
              <Button
                size="small"
                startIcon={<Icon name="add" />}
                disabled={saving}
                onClick={() => setOptions((prev) => [...prev, ''])}
                sx={{ alignSelf: 'flex-start' }}
              >
                Add choice
              </Button>
              {fieldErrors.options && (
                <FormHelperText error>{fieldErrors.options}</FormHelperText>
              )}
            </Stack>
          )}

          <Stack spacing={0}>
            <FormControlLabel
              control={
                <Switch
                  checked={isMemberWritable}
                  onChange={(e) => setIsMemberWritable(e.target.checked)}
                  disabled={saving}
                  slotProps={{ input: { 'aria-label': 'Members can edit this field' } }}
                />
              }
              label="Members can edit this field"
            />
            <FormHelperText>
              {isMemberWritable
                ? 'On — this field appears in the member’s Preferences card on their profile, and they can see and change it.'
                : 'Off (default) — hidden from members; only staff see it. Members get a Preferences card only when at least one field is on.'}
            </FormHelperText>
          </Stack>

          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => setAdvancedOpen((v) => !v)}
            sx={{ alignSelf: 'flex-start' }}
            aria-expanded={advancedOpen}
          >
            {advancedOpen ? 'Hide advanced settings' : 'Advanced settings'}
          </Link>
          <Collapse in={advancedOpen}>
            <Stack spacing={2.5} sx={{ pt: 0.5 }}>
              <TextField
                label="Field key"
                value={keyTouched ? fieldKey : effectiveKey}
                onChange={(e) => {
                  setKeyTouched(true);
                  setFieldKey(e.target.value);
                }}
                disabled={saving}
                size="small"
                fullWidth
                error={Boolean(fieldErrors.fieldKey)}
                helperText={
                  fieldErrors.fieldKey ??
                  'The stable key this field is stored under — derived from the label; edit only if you need a specific key.'
                }
                slotProps={{ htmlInput: { 'aria-label': 'Field key' } }}
              />
              <FormControl size="small" fullWidth disabled={saving}>
                <InputLabel id={ownershipLabelId}>Ownership</InputLabel>
                <Select
                  labelId={ownershipLabelId}
                  label="Ownership"
                  value={fieldOwnership}
                  onChange={(e) => setFieldOwnership(e.target.value as FieldOwnership)}
                  slotProps={{ input: { 'aria-label': 'Ownership' } }}
                >
                  {FIELD_OWNERSHIPS.map((ownership) => (
                    <MenuItem key={ownership} value={ownership}>
                      {fieldOwnershipLabel(ownership)}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{ownershipHelp(fieldOwnership)}</FormHelperText>
              </FormControl>
              {fieldOwnership !== 'DINK' && (
                <TextField
                  label={CONTACT_SYSTEM_FIELD_KEY_LABEL}
                  value={contactKey}
                  onChange={(e) => setContactKey(e.target.value)}
                  disabled={saving}
                  size="small"
                  fullWidth
                  helperText="Where this field is mirrored in / read from the contact system."
                  slotProps={{ htmlInput: { 'aria-label': CONTACT_SYSTEM_FIELD_KEY_LABEL } }}
                />
              )}
            </Stack>
          </Collapse>

          {duplicateKey && (
            // Visible whether or not Advanced is open — the derived key can
            // collide without the operator ever seeing the key input.
            <Alert severity="warning" role="status">
              A field with this key already exists — saving will be rejected.
            </Alert>
          )}

          {serverError && (
            <Alert severity="error" role="alert">
              {serverError}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Save field' : 'Add field'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
